// Medical Scores Database - Part 4: More Cardiology, Internal Medicine, Nutrition
// Additional specialized scores

import {
  Brain,
  Heart,
  Activity,
  Droplets,
  Scale,
  Thermometer,
  Beaker,
  User,
  Zap,
  TrendingUp,
  Gauge,
  Calculator
} from 'lucide-react';

// ==================== CARDIOLOGIE COMPLÉMENTAIRE ====================
export const CARDIOLOGY_MORE_SCORES = {
  wellsTVP: {
    name: 'Wells TVP',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Probabilité thrombose veineuse profonde',
    color: 'red',
    fields: [
      { id: 'cancer', label: 'Cancer actif', type: 'checkbox', points: 1 },
      { id: 'paralysis', label: 'Paralysie/parésie MI', type: 'checkbox', points: 1 },
      { id: 'bedridden', label: 'Alitement > 3j ou chirurgie < 12 sem', type: 'checkbox', points: 1 },
      { id: 'tenderness', label: 'Douleur trajet veineux', type: 'checkbox', points: 1 },
      { id: 'swelling', label: 'Œdème de tout le MI', type: 'checkbox', points: 1 },
      { id: 'calf', label: 'Mollet > 3cm vs controlatéral', type: 'checkbox', points: 1 },
      { id: 'pitting', label: 'Œdème prenant godet', type: 'checkbox', points: 1 },
      { id: 'collateral', label: 'Veines collatérales', type: 'checkbox', points: 1 },
      { id: 'history', label: 'ATCD TVP documentée', type: 'checkbox', points: 1 },
      { id: 'alternative', label: 'Diagnostic alternatif probable', type: 'checkbox', points: -2 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.cancer) total += 1;
      if (values.paralysis) total += 1;
      if (values.bedridden) total += 1;
      if (values.tenderness) total += 1;
      if (values.swelling) total += 1;
      if (values.calf) total += 1;
      if (values.pitting) total += 1;
      if (values.collateral) total += 1;
      if (values.history) total += 1;
      if (values.alternative) total -= 2;
      return { score: total, max: 9 };
    },
    interpret: (score) => {
      if (score < 1) return { level: 'success', text: 'Probabilité faible (3%)', detail: 'D-Dimères, si négatifs: TVP exclue' };
      if (score <= 2) return { level: 'warning', text: 'Probabilité modérée (17%)', detail: 'D-Dimères puis écho-doppler si positifs' };
      return { level: 'danger', text: 'Probabilité forte (75%)', detail: 'Écho-doppler directement' };
    },
    formula: 'Wells TVP = Somme critères - 2 si diagnostic alternatif probable'
  },

  chads2: {
    name: 'CHADS₂',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Risque embolique sur FA (ancienne version)',
    color: 'red',
    fields: [
      { id: 'chf', label: 'Insuffisance cardiaque', type: 'checkbox', points: 1 },
      { id: 'htn', label: 'HTA', type: 'checkbox', points: 1 },
      { id: 'age75', label: 'Âge ≥ 75 ans', type: 'checkbox', points: 1 },
      { id: 'diabetes', label: 'Diabète', type: 'checkbox', points: 1 },
      { id: 'stroke', label: 'AVC/AIT (2 points)', type: 'checkbox', points: 2 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.chf) total += 1;
      if (values.htn) total += 1;
      if (values.age75) total += 1;
      if (values.diabetes) total += 1;
      if (values.stroke) total += 2;
      return { score: total, max: 6 };
    },
    interpret: (score) => {
      const risks = ['1.9%', '2.8%', '4%', '5.9%', '8.5%', '12.5%', '18.2%'];
      if (score === 0) return { level: 'success', text: 'CHADS₂ 0', detail: `Risque AVC/an: ${risks[0]}. Aspirine ou rien` };
      if (score === 1) return { level: 'warning', text: 'CHADS₂ 1', detail: `Risque AVC/an: ${risks[1]}. Aspirine ou AOD` };
      return { level: 'danger', text: `CHADS₂ ${score}`, detail: `Risque AVC/an: ${risks[Math.min(score, 6)]}. AOD recommandé` };
    },
    formula: 'CHADS₂ = Cardiac failure + Hypertension + Age + Diabetes + Stroke(×2)'
  },

  framingham: {
    name: 'Score de Framingham',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Risque cardiovasculaire à 10 ans',
    color: 'red',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 20, max: 79 },
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 'M', label: 'Homme' }, { value: 'F', label: 'Femme' }
      ]},
      { id: 'cholTotal', label: 'Cholestérol total (mg/dL)', type: 'number', min: 100, max: 400 },
      { id: 'hdl', label: 'HDL (mg/dL)', type: 'number', min: 20, max: 100 },
      { id: 'sbp', label: 'PAS (mmHg)', type: 'number', min: 90, max: 200 },
      { id: 'treated', label: 'HTA traitée', type: 'checkbox', points: 0 },
      { id: 'smoker', label: 'Fumeur actuel', type: 'checkbox', points: 0 },
      { id: 'diabetes', label: 'Diabète', type: 'checkbox', points: 0 }
    ],
    calculate: (values) => {
      // Simplified calculation
      const age = values.age || 50;
      const isMale = values.sex === 'M';
      let points = 0;
      
      // Age points
      if (isMale) {
        if (age < 35) points += 0;
        else if (age < 40) points += 2;
        else if (age < 45) points += 5;
        else if (age < 50) points += 6;
        else if (age < 55) points += 8;
        else if (age < 60) points += 10;
        else if (age < 65) points += 11;
        else if (age < 70) points += 12;
        else points += 14;
      } else {
        if (age < 35) points += 0;
        else if (age < 40) points += 2;
        else if (age < 45) points += 4;
        else if (age < 50) points += 5;
        else if (age < 55) points += 7;
        else if (age < 60) points += 8;
        else if (age < 65) points += 9;
        else if (age < 70) points += 10;
        else points += 11;
      }
      
      // Smoking
      if (values.smoker) points += isMale ? 4 : 3;
      
      // Diabetes
      if (values.diabetes) points += isMale ? 3 : 4;
      
      return { score: points, max: 'points' };
    },
    interpret: (score) => {
      if (score < 10) return { level: 'success', text: `Framingham ${score} pts`, detail: 'Risque CV faible (< 10% à 10 ans)' };
      if (score < 15) return { level: 'warning', text: `Framingham ${score} pts`, detail: 'Risque CV modéré (10-20% à 10 ans)' };
      return { level: 'danger', text: `Framingham ${score} pts`, detail: 'Risque CV élevé (> 20% à 10 ans). Prévention intensive' };
    },
    formula: 'Framingham = Âge + Cholestérol + HDL + PA + Tabac + Diabète'
  },

  crusade: {
    name: 'Score CRUSADE',
    category: 'Cardiologie',
    icon: Droplets,
    description: 'Risque hémorragique SCA non ST+',
    color: 'red',
    fields: [
      { id: 'hematocrit', label: 'Hématocrite (%)', type: 'select', options: [
        { value: 9, label: '< 31% (9)' }, { value: 7, label: '31-33.9% (7)' },
        { value: 3, label: '34-36.9% (3)' }, { value: 2, label: '37-39.9% (2)' }, { value: 0, label: '≥ 40% (0)' }
      ]},
      { id: 'clcr', label: 'Clairance créatinine', type: 'select', options: [
        { value: 39, label: '< 15 mL/min (39)' }, { value: 35, label: '15-30 (35)' },
        { value: 28, label: '30-60 (28)' }, { value: 17, label: '60-90 (17)' },
        { value: 7, label: '90-120 (7)' }, { value: 0, label: '> 120 (0)' }
      ]},
      { id: 'hr', label: 'FC', type: 'select', options: [
        { value: 0, label: '≤ 70/min (0)' }, { value: 1, label: '71-80 (1)' },
        { value: 3, label: '81-90 (3)' }, { value: 6, label: '91-100 (6)' },
        { value: 8, label: '101-110 (8)' }, { value: 10, label: '111-120 (10)' }, { value: 11, label: '> 120 (11)' }
      ]},
      { id: 'sex', label: 'Sexe féminin', type: 'checkbox', points: 8 },
      { id: 'chf', label: 'Signes d\'insuffisance cardiaque', type: 'checkbox', points: 7 },
      { id: 'vascular', label: 'Maladie vasculaire', type: 'checkbox', points: 6 },
      { id: 'diabetes', label: 'Diabète', type: 'checkbox', points: 6 },
      { id: 'sbp', label: 'PAS', type: 'select', options: [
        { value: 10, label: '≤ 90 mmHg (10)' }, { value: 8, label: '91-100 (8)' },
        { value: 5, label: '101-120 (5)' }, { value: 1, label: '121-180 (1)' },
        { value: 3, label: '181-200 (3)' }, { value: 5, label: '> 200 (5)' }
      ]}
    ],
    calculate: (values) => {
      let total = (values.hematocrit || 0) + (values.clcr || 0) + (values.hr || 0) + (values.sbp || 0);
      if (values.sex) total += 8;
      if (values.chf) total += 7;
      if (values.vascular) total += 6;
      if (values.diabetes) total += 6;
      return { score: total, max: 96 };
    },
    interpret: (score) => {
      if (score <= 20) return { level: 'success', text: `CRUSADE ${score}`, detail: 'Risque très faible (3.1%)' };
      if (score <= 30) return { level: 'success', text: `CRUSADE ${score}`, detail: 'Risque faible (5.5%)' };
      if (score <= 40) return { level: 'warning', text: `CRUSADE ${score}`, detail: 'Risque modéré (8.6%)' };
      if (score <= 50) return { level: 'danger', text: `CRUSADE ${score}`, detail: 'Risque élevé (11.9%)' };
      return { level: 'danger', text: `CRUSADE ${score}`, detail: 'Risque très élevé (19.5%)' };
    },
    formula: 'CRUSADE = Hématocrite + Clairance + FC + Sexe + IC + Vasc + Diabète + PAS'
  },

  euroScore: {
    name: 'EuroSCORE II (simplifié)',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Risque chirurgie cardiaque',
    color: 'red',
    fields: [
      { id: 'age', label: 'Âge', type: 'number', min: 18, max: 100 },
      { id: 'sex', label: 'Sexe féminin', type: 'checkbox', points: 1 },
      { id: 'creatinine', label: 'Créatinine > 200 µmol/L', type: 'checkbox', points: 2 },
      { id: 'dialysis', label: 'Dialyse', type: 'checkbox', points: 3 },
      { id: 'arteriopathy', label: 'Artériopathie', type: 'checkbox', points: 2 },
      { id: 'mobility', label: 'Mobilité réduite', type: 'checkbox', points: 2 },
      { id: 'cardiac', label: 'Réintervention cardiaque', type: 'checkbox', points: 3 },
      { id: 'endocarditis', label: 'Endocardite active', type: 'checkbox', points: 3 },
      { id: 'critical', label: 'État critique préopératoire', type: 'checkbox', points: 4 },
      { id: 'lvef', label: 'FEVG', type: 'select', options: [
        { value: 0, label: '> 50% (0)' }, { value: 1, label: '31-50% (1)' },
        { value: 2, label: '21-30% (2)' }, { value: 3, label: '≤ 20% (3)' }
      ]},
      { id: 'urgency', label: 'Urgence', type: 'select', options: [
        { value: 0, label: 'Élective (0)' }, { value: 1, label: 'Urgente (1)' },
        { value: 2, label: 'Urgence immédiate (2)' }, { value: 4, label: 'Sauvetage (4)' }
      ]}
    ],
    calculate: (values) => {
      let total = 0;
      const age = values.age || 50;
      if (age > 60) total += Math.floor((age - 60) / 5);
      if (values.sex) total += 1;
      if (values.creatinine) total += 2;
      if (values.dialysis) total += 3;
      if (values.arteriopathy) total += 2;
      if (values.mobility) total += 2;
      if (values.cardiac) total += 3;
      if (values.endocarditis) total += 3;
      if (values.critical) total += 4;
      total += (values.lvef || 0);
      total += (values.urgency || 0);
      return { score: total, max: 'variable' };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `EuroSCORE ${score}`, detail: 'Risque faible (mortalité < 2%)' };
      if (score <= 5) return { level: 'warning', text: `EuroSCORE ${score}`, detail: 'Risque modéré (mortalité 2-5%)' };
      return { level: 'danger', text: `EuroSCORE ${score}`, detail: 'Risque élevé (mortalité > 5%)' };
    },
    formula: 'EuroSCORE II simplifié - version complète requiert calculateur'
  }
};

// ==================== MÉDECINE INTERNE ====================
export const INTERNAL_MEDICINE_SCORES = {
  charlson: {
    name: 'Index de Charlson',
    category: 'Médecine Interne',
    icon: User,
    description: 'Survie selon comorbidités',
    color: 'slate',
    fields: [
      { id: 'mi', label: 'Infarctus du myocarde', type: 'checkbox', points: 1 },
      { id: 'chf', label: 'Insuffisance cardiaque', type: 'checkbox', points: 1 },
      { id: 'pvd', label: 'Artériopathie périphérique', type: 'checkbox', points: 1 },
      { id: 'cva', label: 'AVC non invalidant', type: 'checkbox', points: 1 },
      { id: 'dementia', label: 'Démence', type: 'checkbox', points: 1 },
      { id: 'copd', label: 'BPCO', type: 'checkbox', points: 1 },
      { id: 'connective', label: 'Connectivite', type: 'checkbox', points: 1 },
      { id: 'ulcer', label: 'Ulcère gastro-duodénal', type: 'checkbox', points: 1 },
      { id: 'liverMild', label: 'Hépatopathie légère', type: 'checkbox', points: 1 },
      { id: 'diabetes', label: 'Diabète sans complication', type: 'checkbox', points: 1 },
      { id: 'hemiplegia', label: 'Hémiplégie', type: 'checkbox', points: 2 },
      { id: 'renal', label: 'Insuffisance rénale modérée/sévère', type: 'checkbox', points: 2 },
      { id: 'diabetesOrgan', label: 'Diabète avec atteinte organe', type: 'checkbox', points: 2 },
      { id: 'tumor', label: 'Tumeur solide (< 5 ans)', type: 'checkbox', points: 2 },
      { id: 'leukemia', label: 'Leucémie', type: 'checkbox', points: 2 },
      { id: 'lymphoma', label: 'Lymphome', type: 'checkbox', points: 2 },
      { id: 'liverModerate', label: 'Hépatopathie modérée/sévère', type: 'checkbox', points: 3 },
      { id: 'metastatic', label: 'Tumeur métastatique', type: 'checkbox', points: 6 },
      { id: 'aids', label: 'SIDA', type: 'checkbox', points: 6 },
      { id: 'age', label: 'Âge', type: 'select', options: [
        { value: 0, label: '< 50 ans (0)' }, { value: 1, label: '50-59 (1)' },
        { value: 2, label: '60-69 (2)' }, { value: 3, label: '70-79 (3)' }, { value: 4, label: '≥ 80 (4)' }
      ]}
    ],
    calculate: (values) => {
      let total = values.age || 0;
      if (values.mi) total += 1;
      if (values.chf) total += 1;
      if (values.pvd) total += 1;
      if (values.cva) total += 1;
      if (values.dementia) total += 1;
      if (values.copd) total += 1;
      if (values.connective) total += 1;
      if (values.ulcer) total += 1;
      if (values.liverMild) total += 1;
      if (values.diabetes) total += 1;
      if (values.hemiplegia) total += 2;
      if (values.renal) total += 2;
      if (values.diabetesOrgan) total += 2;
      if (values.tumor) total += 2;
      if (values.leukemia) total += 2;
      if (values.lymphoma) total += 2;
      if (values.liverModerate) total += 3;
      if (values.metastatic) total += 6;
      if (values.aids) total += 6;
      return { score: total, max: 'variable' };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: `Charlson ${score}`, detail: 'Survie à 10 ans: 99%' };
      if (score <= 2) return { level: 'success', text: `Charlson ${score}`, detail: 'Survie à 10 ans: 90%' };
      if (score <= 4) return { level: 'warning', text: `Charlson ${score}`, detail: 'Survie à 10 ans: 53%' };
      if (score <= 6) return { level: 'danger', text: `Charlson ${score}`, detail: 'Survie à 10 ans: 21%' };
      return { level: 'danger', text: `Charlson ${score}`, detail: 'Survie à 10 ans: < 2%' };
    },
    formula: 'Charlson = Âge + Somme des comorbidités pondérées'
  },

  calcemieCorrigee: {
    name: 'Calcémie corrigée',
    category: 'Médecine Interne',
    icon: Beaker,
    description: 'Correction calcémie selon albumine',
    color: 'slate',
    fields: [
      { id: 'calcium', label: 'Calcémie (mmol/L)', type: 'number', min: 1, max: 5, step: 0.01 },
      { id: 'albumin', label: 'Albumine (g/L)', type: 'number', min: 10, max: 60 }
    ],
    calculate: (values) => {
      const ca = values.calcium || 2.4;
      const alb = values.albumin || 40;
      const corrected = ca + 0.02 * (40 - alb);
      return { score: corrected.toFixed(2), max: 'mmol/L' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 2.2) return { level: 'danger', text: `Ca corrigée: ${score} mmol/L`, detail: 'Hypocalcémie' };
      if (s < 2.5) return { level: 'success', text: `Ca corrigée: ${score} mmol/L`, detail: 'Normal' };
      if (s < 2.75) return { level: 'warning', text: `Ca corrigée: ${score} mmol/L`, detail: 'Hypercalcémie légère' };
      if (s < 3.0) return { level: 'danger', text: `Ca corrigée: ${score} mmol/L`, detail: 'Hypercalcémie modérée' };
      return { level: 'danger', text: `Ca corrigée: ${score} mmol/L`, detail: 'Hypercalcémie sévère. Urgence!' };
    },
    formula: 'Ca corrigée = Ca mesurée + 0.02 × (40 - Albumine)'
  },

  natremieCorrigee: {
    name: 'Natrémie corrigée',
    category: 'Médecine Interne',
    icon: Beaker,
    description: 'Correction natrémie selon glycémie',
    color: 'slate',
    fields: [
      { id: 'sodium', label: 'Natrémie (mmol/L)', type: 'number', min: 100, max: 180 },
      { id: 'glucose', label: 'Glycémie (mmol/L)', type: 'number', min: 1, max: 100 }
    ],
    calculate: (values) => {
      const na = values.sodium || 140;
      const glu = values.glucose || 5;
      // Formule de Katz: +1.6 mmol/L de Na pour chaque 5.6 mmol/L de glucose au-dessus de 5.6
      const corrected = na + 0.3 * (glu - 5.6);
      return { score: corrected.toFixed(1), max: 'mmol/L' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 135) return { level: 'warning', text: `Na corrigée: ${score} mmol/L`, detail: 'Hyponatrémie vraie' };
      if (s <= 145) return { level: 'success', text: `Na corrigée: ${score} mmol/L`, detail: 'Normal' };
      return { level: 'warning', text: `Na corrigée: ${score} mmol/L`, detail: 'Hypernatrémie' };
    },
    formula: 'Na corrigée = Na + 0.3 × (Glycémie - 5.6)'
  },

  deficitEauLibre: {
    name: 'Déficit en eau libre',
    category: 'Médecine Interne',
    icon: Droplets,
    description: 'Calcul déficit sur hypernatrémie',
    color: 'slate',
    fields: [
      { id: 'weight', label: 'Poids (kg)', type: 'number', min: 30, max: 200 },
      { id: 'sodium', label: 'Natrémie actuelle (mmol/L)', type: 'number', min: 145, max: 200 },
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 0.6, label: 'Homme (0.6)' }, { value: 0.5, label: 'Femme (0.5)' }
      ]}
    ],
    calculate: (values) => {
      const weight = values.weight || 70;
      const na = values.sodium || 150;
      const coef = values.sex || 0.6;
      const deficit = weight * coef * ((na / 140) - 1);
      return { score: deficit.toFixed(1), max: 'L' };
    },
    interpret: (score) => {
      return { level: 'warning', text: `Déficit: ${score} L`, detail: 'Corriger lentement (max 10-12 mmol/L/24h de natrémie)' };
    },
    formula: 'Déficit = Poids × Coef × [(Na actuelle / 140) - 1]'
  },

  trouAnionique: {
    name: 'Trou anionique',
    category: 'Médecine Interne',
    icon: Beaker,
    description: 'Calcul trou anionique plasmatique',
    color: 'slate',
    fields: [
      { id: 'sodium', label: 'Natrémie (mmol/L)', type: 'number', min: 120, max: 160 },
      { id: 'chloride', label: 'Chlorémie (mmol/L)', type: 'number', min: 80, max: 130 },
      { id: 'bicarbonate', label: 'Bicarbonates (mmol/L)', type: 'number', min: 5, max: 40 },
      { id: 'albumin', label: 'Albumine (g/L) - optionnel', type: 'number', min: 10, max: 60 }
    ],
    calculate: (values) => {
      const na = values.sodium || 140;
      const cl = values.chloride || 105;
      const hco3 = values.bicarbonate || 24;
      const alb = values.albumin || 40;
      const ta = na - (cl + hco3);
      // Correction pour l'albumine: +2.5 mmol/L pour chaque 10 g/L de baisse d'albumine
      const taCorr = ta + 2.5 * ((40 - alb) / 10);
      return { score: ta, max: 'mmol/L', details: { corrected: taCorr.toFixed(1) } };
    },
    interpret: (score, result) => {
      const taCorr = result?.details?.corrected || score;
      if (score < 8) return { level: 'warning', text: `TA: ${score} mmol/L`, detail: `TA corrigé: ${taCorr}. TA bas (myélome, intox bromures)` };
      if (score <= 12) return { level: 'success', text: `TA: ${score} mmol/L`, detail: `TA corrigé: ${taCorr}. Normal (8-12)` };
      if (score <= 20) return { level: 'warning', text: `TA: ${score} mmol/L`, detail: `TA corrigé: ${taCorr}. TA augmenté modéré` };
      return { level: 'danger', text: `TA: ${score} mmol/L`, detail: `TA corrigé: ${taCorr}. TA très élevé (acidose lactique, ACD, IRC, intox)` };
    },
    formula: 'TA = Na - (Cl + HCO3). Normal: 8-12. TA corrigé = TA + 2.5 × [(40-Alb)/10]'
  },

  homaIR: {
    name: 'HOMA-IR',
    category: 'Médecine Interne',
    icon: Beaker,
    description: 'Insulino-résistance',
    color: 'slate',
    fields: [
      { id: 'glucose', label: 'Glycémie à jeun (mmol/L)', type: 'number', min: 2, max: 30, step: 0.1 },
      { id: 'insulin', label: 'Insulinémie à jeun (µUI/mL)', type: 'number', min: 1, max: 100 }
    ],
    calculate: (values) => {
      const glu = values.glucose || 5;
      const ins = values.insulin || 10;
      const homa = (glu * ins) / 22.5;
      return { score: homa.toFixed(2), max: '' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 2.5) return { level: 'success', text: `HOMA-IR: ${score}`, detail: 'Sensibilité insuline normale' };
      if (s < 4) return { level: 'warning', text: `HOMA-IR: ${score}`, detail: 'Insulino-résistance modérée' };
      return { level: 'danger', text: `HOMA-IR: ${score}`, detail: 'Insulino-résistance importante' };
    },
    formula: 'HOMA-IR = (Glycémie × Insulinémie) / 22.5'
  }
};

// ==================== NUTRITION ====================
export const NUTRITION_SCORES = {
  bmi: {
    name: 'IMC (BMI)',
    category: 'Nutrition',
    icon: Scale,
    description: 'Indice de masse corporelle',
    color: 'emerald',
    fields: [
      { id: 'weight', label: 'Poids (kg)', type: 'number', min: 20, max: 300 },
      { id: 'height', label: 'Taille (cm)', type: 'number', min: 100, max: 250 }
    ],
    calculate: (values) => {
      const weight = values.weight || 70;
      const height = (values.height || 170) / 100;
      const bmi = weight / (height * height);
      return { score: bmi.toFixed(1), max: 'kg/m²' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 16.5) return { level: 'danger', text: `IMC: ${score}`, detail: 'Dénutrition sévère' };
      if (s < 18.5) return { level: 'warning', text: `IMC: ${score}`, detail: 'Maigreur' };
      if (s < 25) return { level: 'success', text: `IMC: ${score}`, detail: 'Poids normal' };
      if (s < 30) return { level: 'warning', text: `IMC: ${score}`, detail: 'Surpoids' };
      if (s < 35) return { level: 'danger', text: `IMC: ${score}`, detail: 'Obésité grade I' };
      if (s < 40) return { level: 'danger', text: `IMC: ${score}`, detail: 'Obésité grade II' };
      return { level: 'danger', text: `IMC: ${score}`, detail: 'Obésité morbide (grade III)' };
    },
    formula: 'IMC = Poids / Taille²'
  },

  poidsIdeal: {
    name: 'Poids idéal théorique',
    category: 'Nutrition',
    icon: Scale,
    description: 'Calcul poids idéal (Lorentz)',
    color: 'emerald',
    fields: [
      { id: 'height', label: 'Taille (cm)', type: 'number', min: 100, max: 250 },
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 'M', label: 'Homme' }, { value: 'F', label: 'Femme' }
      ]}
    ],
    calculate: (values) => {
      const h = values.height || 170;
      const isMale = values.sex === 'M';
      // Formule de Lorentz
      const pit = isMale ? h - 100 - ((h - 150) / 4) : h - 100 - ((h - 150) / 2.5);
      return { score: pit.toFixed(1), max: 'kg' };
    },
    interpret: (score) => {
      return { level: 'success', text: `Poids idéal: ${score} kg`, detail: 'Formule de Lorentz' };
    },
    formula: 'Homme: T - 100 - (T-150)/4 | Femme: T - 100 - (T-150)/2.5'
  },

  nrs2002: {
    name: 'NRS-2002',
    category: 'Nutrition',
    icon: Scale,
    description: 'Dépistage dénutrition hospitalière',
    color: 'emerald',
    fields: [
      { id: 'nutritionalStatus', label: 'État nutritionnel', type: 'select', options: [
        { value: 0, label: '0 - Normal' },
        { value: 1, label: '1 - Perte > 5% en 3 mois ou apports 50-75%' },
        { value: 2, label: '2 - Perte > 5% en 2 mois ou IMC 18.5-20.5 ou apports 25-60%' },
        { value: 3, label: '3 - Perte > 5% en 1 mois ou IMC < 18.5 ou apports < 25%' }
      ]},
      { id: 'severity', label: 'Sévérité maladie', type: 'select', options: [
        { value: 0, label: '0 - Normale' },
        { value: 1, label: '1 - Fracture hanche, diabète, oncologie' },
        { value: 2, label: '2 - Chirurgie majeure abdominale, AVC' },
        { value: 3, label: '3 - Réanimation (APACHE > 10)' }
      ]},
      { id: 'age70', label: 'Âge ≥ 70 ans', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = (values.nutritionalStatus || 0) + (values.severity || 0);
      if (values.age70) total += 1;
      return { score: total, max: 7 };
    },
    interpret: (score) => {
      if (score < 3) return { level: 'success', text: `NRS-2002: ${score}`, detail: 'Pas de risque nutritionnel. Réévaluer à 1 semaine' };
      return { level: 'danger', text: `NRS-2002: ${score}`, detail: 'Risque nutritionnel. Support nutritionnel recommandé' };
    },
    formula: 'NRS-2002 ≥ 3 = risque nutritionnel'
  },

  ldlFriedewald: {
    name: 'LDL-Cholestérol (Friedewald)',
    category: 'Nutrition',
    icon: Beaker,
    description: 'Calcul LDL-C',
    color: 'emerald',
    fields: [
      { id: 'cholTotal', label: 'Cholestérol total (mmol/L)', type: 'number', min: 2, max: 15, step: 0.1 },
      { id: 'hdl', label: 'HDL-C (mmol/L)', type: 'number', min: 0.5, max: 5, step: 0.1 },
      { id: 'tg', label: 'Triglycérides (mmol/L)', type: 'number', min: 0.3, max: 10, step: 0.1 }
    ],
    calculate: (values) => {
      const ct = values.cholTotal || 5;
      const hdl = values.hdl || 1.3;
      const tg = values.tg || 1.5;
      if (tg > 4.5) {
        return { score: 'N/A', max: '', details: { error: true } };
      }
      const ldl = ct - hdl - (tg / 2.2);
      return { score: ldl.toFixed(2), max: 'mmol/L' };
    },
    interpret: (score, result) => {
      if (result?.details?.error) return { level: 'warning', text: 'Non calculable', detail: 'TG > 4.5 mmol/L: formule non applicable' };
      const s = parseFloat(score);
      if (s < 1.8) return { level: 'success', text: `LDL: ${score} mmol/L`, detail: 'Optimal (cible très haut risque CV)' };
      if (s < 2.6) return { level: 'success', text: `LDL: ${score} mmol/L`, detail: 'Cible haut risque CV' };
      if (s < 3.0) return { level: 'warning', text: `LDL: ${score} mmol/L`, detail: 'Limite supérieure' };
      if (s < 4.0) return { level: 'warning', text: `LDL: ${score} mmol/L`, detail: 'Élevé' };
      return { level: 'danger', text: `LDL: ${score} mmol/L`, detail: 'Très élevé' };
    },
    formula: 'LDL = CT - HDL - TG/2.2 (si TG < 4.5 mmol/L)'
  },

  depenseEnergetique: {
    name: 'Dépense énergétique de base',
    category: 'Nutrition',
    icon: Zap,
    description: 'Harris-Benedict / Mifflin',
    color: 'emerald',
    fields: [
      { id: 'weight', label: 'Poids (kg)', type: 'number', min: 30, max: 250 },
      { id: 'height', label: 'Taille (cm)', type: 'number', min: 100, max: 250 },
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 18, max: 120 },
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 'M', label: 'Homme' }, { value: 'F', label: 'Femme' }
      ]},
      { id: 'activity', label: 'Niveau d\'activité', type: 'select', options: [
        { value: 1.2, label: 'Sédentaire (1.2)' },
        { value: 1.375, label: 'Activité légère (1.375)' },
        { value: 1.55, label: 'Activité modérée (1.55)' },
        { value: 1.725, label: 'Activité intense (1.725)' },
        { value: 1.9, label: 'Activité très intense (1.9)' }
      ]}
    ],
    calculate: (values) => {
      const w = values.weight || 70;
      const h = values.height || 170;
      const age = values.age || 40;
      const isMale = values.sex === 'M';
      const activity = values.activity || 1.2;
      
      // Mifflin-St Jeor
      let bmr;
      if (isMale) {
        bmr = 10 * w + 6.25 * h - 5 * age + 5;
      } else {
        bmr = 10 * w + 6.25 * h - 5 * age - 161;
      }
      const tdee = bmr * activity;
      return { score: Math.round(tdee), max: 'kcal/j', details: { bmr: Math.round(bmr) } };
    },
    interpret: (score, result) => {
      const bmr = result?.details?.bmr || 0;
      return { level: 'success', text: `Besoins: ${score} kcal/j`, detail: `Métabolisme de base: ${bmr} kcal/j (Mifflin-St Jeor)` };
    },
    formula: 'Mifflin: H: 10×P + 6.25×T - 5×A + 5 | F: 10×P + 6.25×T - 5×A - 161'
  }
};

// ==================== SOINS INFIRMIERS ====================
export const NURSING_SCORES = {
  braden: {
    name: 'Échelle de Braden',
    category: 'Soins Infirmiers',
    icon: User,
    description: 'Risque d\'escarre',
    color: 'sky',
    fields: [
      { id: 'sensoryPerception', label: 'Perception sensorielle', type: 'select', options: [
        { value: 1, label: '1 - Complètement limité' }, { value: 2, label: '2 - Très limité' },
        { value: 3, label: '3 - Légèrement limité' }, { value: 4, label: '4 - Pas d\'altération' }
      ]},
      { id: 'moisture', label: 'Humidité', type: 'select', options: [
        { value: 1, label: '1 - Constamment humide' }, { value: 2, label: '2 - Souvent humide' },
        { value: 3, label: '3 - Occasionnellement humide' }, { value: 4, label: '4 - Rarement humide' }
      ]},
      { id: 'activity', label: 'Activité', type: 'select', options: [
        { value: 1, label: '1 - Alité' }, { value: 2, label: '2 - Fauteuil' },
        { value: 3, label: '3 - Marche occasionnelle' }, { value: 4, label: '4 - Marche fréquente' }
      ]},
      { id: 'mobility', label: 'Mobilité', type: 'select', options: [
        { value: 1, label: '1 - Complètement immobile' }, { value: 2, label: '2 - Très limité' },
        { value: 3, label: '3 - Légèrement limité' }, { value: 4, label: '4 - Pas de limitation' }
      ]},
      { id: 'nutrition', label: 'Nutrition', type: 'select', options: [
        { value: 1, label: '1 - Très pauvre' }, { value: 2, label: '2 - Probablement inadéquate' },
        { value: 3, label: '3 - Adéquate' }, { value: 4, label: '4 - Excellente' }
      ]},
      { id: 'friction', label: 'Friction et cisaillement', type: 'select', options: [
        { value: 1, label: '1 - Problème' }, { value: 2, label: '2 - Problème potentiel' },
        { value: 3, label: '3 - Pas de problème apparent' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 23 };
    },
    interpret: (score) => {
      if (score >= 19) return { level: 'success', text: `Braden ${score}`, detail: 'Risque faible' };
      if (score >= 15) return { level: 'warning', text: `Braden ${score}`, detail: 'Risque modéré. Prévention renforcée' };
      if (score >= 13) return { level: 'danger', text: `Braden ${score}`, detail: 'Risque élevé. Matelas préventif' };
      return { level: 'danger', text: `Braden ${score}`, detail: 'Risque très élevé. Matelas alternatif, repositionnement' };
    },
    formula: 'Braden = Somme 6 items (6-23). ≤ 18 = risque'
  },

  norton: {
    name: 'Échelle de Norton',
    category: 'Soins Infirmiers',
    icon: User,
    description: 'Risque d\'escarre (gériatrie)',
    color: 'sky',
    fields: [
      { id: 'physical', label: 'État physique', type: 'select', options: [
        { value: 1, label: '1 - Très mauvais' }, { value: 2, label: '2 - Mauvais' },
        { value: 3, label: '3 - Moyen' }, { value: 4, label: '4 - Bon' }
      ]},
      { id: 'mental', label: 'État mental', type: 'select', options: [
        { value: 1, label: '1 - Stuporeux' }, { value: 2, label: '2 - Confus' },
        { value: 3, label: '3 - Apathique' }, { value: 4, label: '4 - Alerte' }
      ]},
      { id: 'activity', label: 'Activité', type: 'select', options: [
        { value: 1, label: '1 - Alité' }, { value: 2, label: '2 - Fauteuil' },
        { value: 3, label: '3 - Marche avec aide' }, { value: 4, label: '4 - Ambulant' }
      ]},
      { id: 'mobility', label: 'Mobilité', type: 'select', options: [
        { value: 1, label: '1 - Immobile' }, { value: 2, label: '2 - Très limitée' },
        { value: 3, label: '3 - Diminuée' }, { value: 4, label: '4 - Totale' }
      ]},
      { id: 'incontinence', label: 'Incontinence', type: 'select', options: [
        { value: 1, label: '1 - Urinaire et fécale' }, { value: 2, label: '2 - Souvent' },
        { value: 3, label: '3 - Occasionnelle' }, { value: 4, label: '4 - Aucune' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 20 };
    },
    interpret: (score) => {
      if (score >= 18) return { level: 'success', text: `Norton ${score}`, detail: 'Risque faible' };
      if (score >= 14) return { level: 'warning', text: `Norton ${score}`, detail: 'Risque modéré' };
      if (score >= 10) return { level: 'danger', text: `Norton ${score}`, detail: 'Risque élevé' };
      return { level: 'danger', text: `Norton ${score}`, detail: 'Risque très élevé' };
    },
    formula: 'Norton = 5 items × 1-4 (5-20). ≤ 14 = risque'
  },

  eva: {
    name: 'EVA Douleur',
    category: 'Soins Infirmiers',
    icon: Activity,
    description: 'Échelle visuelle analogique',
    color: 'sky',
    fields: [
      { id: 'score', label: 'Score EVA (0-10)', type: 'number', min: 0, max: 10 }
    ],
    calculate: (values) => ({ score: values.score || 0, max: 10 }),
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: `EVA ${score}/10`, detail: 'Pas de douleur' };
      if (score <= 3) return { level: 'success', text: `EVA ${score}/10`, detail: 'Douleur légère. Palier 1' };
      if (score <= 6) return { level: 'warning', text: `EVA ${score}/10`, detail: 'Douleur modérée. Palier 2' };
      return { level: 'danger', text: `EVA ${score}/10`, detail: 'Douleur intense. Palier 3' };
    },
    formula: 'EVA 0-10: évaluation subjective douleur'
  },

  debitPerfusion: {
    name: 'Débit de perfusion',
    category: 'Soins Infirmiers',
    icon: Droplets,
    description: 'Calcul gouttes/min',
    color: 'sky',
    fields: [
      { id: 'volume', label: 'Volume à perfuser (mL)', type: 'number', min: 50, max: 5000 },
      { id: 'time', label: 'Durée (heures)', type: 'number', min: 0.5, max: 24, step: 0.5 },
      { id: 'dropFactor', label: 'Facteur de goutte', type: 'select', options: [
        { value: 20, label: '20 gouttes/mL (standard)' },
        { value: 60, label: '60 gouttes/mL (micro-gouttes)' },
        { value: 15, label: '15 gouttes/mL (transfusion)' }
      ]}
    ],
    calculate: (values) => {
      const vol = values.volume || 500;
      const time = values.time || 4;
      const factor = values.dropFactor || 20;
      const dropsPerMin = (vol * factor) / (time * 60);
      const mlPerH = vol / time;
      return { score: Math.round(dropsPerMin), max: 'gtt/min', details: { mlPerH: Math.round(mlPerH) } };
    },
    interpret: (score, result) => {
      const mlh = result?.details?.mlPerH || 0;
      return { level: 'success', text: `${score} gouttes/min`, detail: `Soit ${mlh} mL/h` };
    },
    formula: 'Gouttes/min = (Volume × Facteur) / (Durée en min)'
  }
};

// Export all scores from this file
export const MORE_SPECIALTY_SCORES = {
  ...CARDIOLOGY_MORE_SCORES,
  ...INTERNAL_MEDICINE_SCORES,
  ...NUTRITION_SCORES,
  ...NURSING_SCORES
};
